'use client';

import {
  Autocomplete as MUIAutocomplete,
  TextField,
  createFilterOptions,
} from '@mui/material';

import type {
  AutocompleteProps,
  FilterOptionsState,
  TextFieldProps,
} from '@mui/material';

type CreatedOption = {
  inputValue: string;
  label: string;
};

export type AutocompleteCreatableProps<TOption> = Omit<
  AutocompleteProps<string | TOption | CreatedOption, false, false, true>,
  | 'renderInput'
  | 'options'
  | 'onChange'
  | 'filterOptions'
  | 'getOptionLabel'
  | 'freeSolo'
> & {
  options: TOption[];
  value?: TOption | null;
  onChange?: (value: TOption | null) => void;
  onCreateOption?: (inputValue: string) => void;
  getOptionLabel: (option: TOption) => string;
  addOptionText?: (inputValue: string) => string;
  noOptionsText?: string;
  label?: string;
  placeholder?: string;
  textFieldProps?: TextFieldProps;
};

export function AutocompleteCreatable<TOption>({
  options,
  value,
  onChange,
  onCreateOption,
  getOptionLabel,
  addOptionText = (inputValue) => `Add "${inputValue}"`,
  noOptionsText = 'No options',
  label,
  placeholder,
  textFieldProps,
  selectOnFocus = true,
  clearOnBlur = true,
  handleHomeEndKeys = true,
  renderOption,
  ...autocompleteProps
}: AutocompleteCreatableProps<TOption>) {
  const { slotProps: textFieldSlotProps, ...restTextFieldProps } =
    textFieldProps ?? {};

  type OptionType = string | TOption | CreatedOption;
  const filter = createFilterOptions<OptionType>();

  const isCreatedOption = (option: unknown): option is CreatedOption =>
    typeof option === 'object' && option !== null && 'inputValue' in option;

  const filterOptions = (
    optionList: OptionType[],
    state: FilterOptionsState<OptionType>,
  ): OptionType[] => {
    const filtered = filter(optionList, state);
    const trimmedInput = state.inputValue.trim();

    if (trimmedInput) {
      const hasExisting = options.some(
        (option) =>
          getOptionLabel(option).toLowerCase() === trimmedInput.toLowerCase(),
      );

      if (!hasExisting) {
        filtered.push({
          inputValue: trimmedInput,
          label: addOptionText(trimmedInput),
        });
      }
    }

    return filtered;
  };

  const getOptionLabelInternal = (option: OptionType) => {
    if (typeof option === 'string') return option;
    if (isCreatedOption(option)) return option.inputValue;
    return getOptionLabel(option);
  };

  const handleChange = (_event: unknown, nextValue: OptionType | null) => {
    if (typeof nextValue === 'string') {
      onCreateOption?.(nextValue);
      onChange?.(null);
      return;
    }

    if (nextValue && isCreatedOption(nextValue)) {
      onCreateOption?.(nextValue.inputValue);
      onChange?.(null);
      return;
    }

    onChange?.(nextValue as TOption | null);
  };

  return (
    <MUIAutocomplete
      {...autocompleteProps}
      freeSolo
      options={options as OptionType[]}
      value={value as OptionType | null | undefined}
      selectOnFocus={selectOnFocus}
      clearOnBlur={clearOnBlur}
      handleHomeEndKeys={handleHomeEndKeys}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabelInternal}
      renderOption={(props, option, state, ownerState) => {
        if (typeof option === 'string') {
          return <li {...props}>{option}</li>;
        }
        if (isCreatedOption(option)) {
          return <li {...props}>{option.label}</li>;
        }

        if (renderOption) {
          return renderOption(props, option, state, ownerState);
        }

        return <li {...props}>{getOptionLabel(option as TOption)}</li>;
      }}
      noOptionsText={noOptionsText}
      onChange={handleChange}
      renderInput={(params) => {
        const { InputProps, InputLabelProps, ...restParams } = params;
        return (
          <TextField
            {...restParams}
            {...restTextFieldProps}
            label={label}
            placeholder={placeholder}
            slotProps={{
              ...textFieldSlotProps,
              input: { ...InputProps, ...textFieldSlotProps?.input },
              inputLabel: {
                ...InputLabelProps,
                ...textFieldSlotProps?.inputLabel,
              },
            }}
          />
        );
      }}
    />
  );
}
